import { createElement, useMemo, useState, type ReactNode } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { SuggestionChat as SuggestionChatView } from './SuggestionChat';
import type { SuggestionChat } from '@shared/suggestion-chat';
import { useLoader } from '@/hooks/use-loader';
import { describeApiError } from '@/lib/api-errors';
import { askAboutSuggestion, closeSuggestionChat, getSuggestionChats } from '@/lib/tasks-api';

/** How often a round's chats are read while an agent answers. */
const POLL_MS = 4000;

const answering = (c: SuggestionChat) => c.status === 'working' || c.messages.some((m) => m.status === 'queued');

/**
 * SF-T-304: the live chats on one round of the AI builder's suggestions (the factory message `eventId`), by
 * suggestion number, read again every few seconds while an agent answers. `ask` sends a question (the first
 * starts the suggestion's chat); it resolves an error to show, or null. Off (`enabled` false): nothing is read.
 */
export function useSuggestionChats(taskId: string, eventId: string, { project, enabled = true }: { project?: string; enabled?: boolean } = {}) {
  // Chats a question just started or carried on, until a read catches up with them.
  const [sent, setSent] = useState<ReadonlyMap<number, SuggestionChat>>(new Map());
  const loader = useLoader((signal) => getSuggestionChats(taskId, eventId, { project, signal }), [taskId, eventId, project], {
    enabled,
    pollMs: POLL_MS,
    // While an agent answers, or a question sent here isn't in a read yet; not on a copy the server has moved past.
    pollWhile: (res) => [...merged(res?.data?.chats, sent).values()].some(answering) || [...sent].some(([n, c]) => ahead(c, res?.data?.chats?.find((l) => l.number === n))),
  });
  const loaded = loader.data?.data?.chats;
  const chats = useMemo(() => merged(loaded, sent), [loaded, sent]);
  const loadError = loader.data?.error ? describeApiError(loader.data, 'Couldn’t read the chats about these suggestions') : null;

  const ask = async (number: number, text: string): Promise<string | null> => {
    const res = await askAboutSuggestion(taskId, eventId, number, text, project);
    if (!res.data) return describeApiError(res, 'Couldn’t send the question');
    const chat = res.data.chat;
    setSent((prev) => new Map(prev).set(number, chat));
    loader.reload();
    return null;
  };

  /** Stops suggestion `number`'s chat; resolves an error to show, or null. */
  const stop = async (number: number): Promise<string | null> => {
    const res = await closeSuggestionChat(taskId, eventId, number, project);
    if (!res.data) return describeApiError(res, 'Couldn’t stop the chat');
    const chat = res.data.chat;
    setSent((prev) => new Map(prev).set(number, chat));
    loader.reload();
    return null;
  };

  return { chats, loadError, ask, stop, reload: loader.reload };
}

/** A chat a question here answered with is ahead of the read: none read yet, or the same chat with fewer messages. */
function ahead(sent: SuggestionChat, read: SuggestionChat | undefined): boolean {
  return !read || (read.session_id === sent.session_id && read.messages.length < sent.messages.length);
}

/** The chats read, by number, with one a question here answered with in place of its read while it's ahead. */
function merged(loaded: SuggestionChat[] | undefined, sent: ReadonlyMap<number, SuggestionChat>): Map<number, SuggestionChat> {
  const out = new Map<number, SuggestionChat>((loaded ?? []).map((c) => [c.number, c]));
  for (const [n, c] of sent) if (ahead(c, out.get(n))) out.set(n, c);
  return out;
}

/**
 * SF-T-304: the live chat under each item of one of the AI builder's rounds (the factory message `eventId`): a
 * suggestion's, or (SF-T-308) a question's before it builds. Undefined without `chat` (a preview).
 */
export function useItemChats(
  eventId: string,
  chat: { taskId: string; project?: string; canAsk: boolean; canStop?: boolean } | undefined
): ((number: number, title: string, decided: boolean) => ReactNode) | undefined {
  const chats = useSuggestionChats(chat?.taskId ?? '', eventId, { project: chat?.project, enabled: Boolean(chat) });
  const me = useAuthStore((s) => s.user);
  if (!chat) return undefined;
  return (number, title, decided) => {
    const c = chats.chats.get(number) ?? null;
    return createElement(SuggestionChatView, {
      title,
      chat: c,
      loadError: chats.loadError,
      onRetry: chats.reload,
      canOpen: Boolean(c?.mine && me?.is_admin),
      onAsk: chat.canAsk && !decided && c?.can_ask !== false ? (text: string) => chats.ask(number, text) : undefined,
      onStop: (chat.canAsk || chat.canStop) && c?.can_ask ? () => chats.stop(number) : undefined,
    });
  };
}
